import { useEffect, useMemo, useState } from "react";
import { getMenu, WPNavNode } from "../content/contentClient";
import { getWpBaseUrl } from "../utils/api";

export type MenuItem = {
  id: string;
  title: string;
  url: string;
  children?: MenuItem[];
};

const getFrontendOrigin = () => {
  if (typeof window === "undefined") return "";
  try {
    return window.location.origin;
  } catch {
    return "";
  }
};

const getWpSiteOrigin = () => {
  try {
    return new URL(getWpBaseUrl()).origin;
  } catch {
    return "";
  }
};

const normalizeMenuUrl = (rawHref: string, wpOrigin: string, frontendOrigin: string) => {
  if (!rawHref) return "#";
  const base = wpOrigin || frontendOrigin || undefined;

  try {
    const target = base ? new URL(rawHref, base) : new URL(rawHref);
    const frontend = frontendOrigin || "";
    const isWpLink = wpOrigin && target.origin === wpOrigin;
    const isFrontendLink = frontend && target.origin === frontend;

    if (isWpLink || isFrontendLink) {
      return `${target.pathname || "/"}${target.search || ""}${target.hash || ""}`;
    }

    return target.href;
  } catch {
    if (rawHref.startsWith("/")) return rawHref;
    return `/${rawHref}`;
  }
};

const findDirectLink = (li: Element) => {
  const direct = li.querySelector(
    ":scope > a, :scope > span > a, :scope > .wp-block-navigation-item__content, :scope > .wp-block-navigation-item__content > a"
  );
  if (direct) {
    const tag = direct.tagName.toLowerCase();
    if (tag === "a") return direct as HTMLAnchorElement;
    const nested = direct.querySelector("a");
    if (nested) return nested as HTMLAnchorElement;
  }
  return li.querySelector("a") as HTMLAnchorElement | null;
};

const getItemLabel = (li: Element, link?: HTMLAnchorElement | null) => {
  if (link) return (link.textContent || "").trim();
  const label = li.querySelector(
    ":scope > .wp-block-navigation-item__label, :scope > .wp-block-navigation-item__content, .wp-block-navigation-item__label"
  );
  return (label?.textContent || "").trim();
};

const pickRootList = (doc: Document): Element | null => {
  const lists = Array.from(doc.querySelectorAll("ul"));
  if (!lists.length) return null;

  const preferred = lists.filter((list) => {
    const className = list.getAttribute("class") || "";
    return className.includes("wp-block-navigation__container") || className.includes("wp-block-page-list");
  });

  const candidates = preferred.length ? preferred : lists;
  let best: Element | null = null;
  let bestCount = -1;

  candidates.forEach((list) => {
    const count = Array.from(list.children).filter((child) => child.tagName.toLowerCase() === "li").length;
    if (count > bestCount) {
      best = list;
      bestCount = count;
    }
  });

  return best;
};

const parseNavHtml = (html: string, wpOrigin: string, frontendOrigin: string): MenuItem[] => {
  if (!html) return [];
  try {
    if (typeof DOMParser === "undefined") return [];
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const buildItems = (list: Element, depth: number): MenuItem[] => {
      const listItems = Array.from(list.children).filter(
        (child) => child.tagName.toLowerCase() === "li"
      );

      return listItems
        .map((li, index) => {
          const link = findDirectLink(li);
          const rawHref = link?.getAttribute("href") || "";
          const title = getItemLabel(li, link);

          if (!title) return null;

          const item: MenuItem = {
            id: rawHref || `nav-${depth}-${index}`,
            title,
            url: normalizeMenuUrl(rawHref, wpOrigin, frontendOrigin)
          };

          if (depth < 1) {
            const subList = li.querySelector(
              ":scope > ul, :scope > .wp-block-navigation__submenu-container"
            );
            if (subList) {
              const children = buildItems(subList, depth + 1);
              if (children.length) item.children = children;
            }
          }

          return item;
        })
        .filter((item): item is MenuItem => Boolean(item));
    };

    const rootList = pickRootList(doc);
    const allLis = Array.from(doc.body.querySelectorAll("li"));
    const topLis = allLis.filter((li) => !li.parentElement?.closest("li"));

    if (topLis.length) {
      const temp = doc.createElement("ul");
      topLis.forEach((li) => temp.appendChild(li.cloneNode(true)));
      return buildItems(temp, 0);
    }

    return rootList ? buildItems(rootList, 0) : [];
  } catch (e) {
    return [];
  }
};

const resolveMenuSlug = (menuSlug: string, lang?: string) => {
  if (lang === "en" && !menuSlug.endsWith("-en")) return `${menuSlug}-en`;
  return menuSlug;
};

export default function useWPMenu(menuSlug = "primary", lang?: string) {
  const [items, setItems] = useState<MenuItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const normalizedSlug = useMemo(() => resolveMenuSlug(menuSlug, lang).toLowerCase(), [menuSlug, lang]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(false);
      try {
        const navs: WPNavNode[] = await getMenu(20, lang);

        const wpOrigin = getWpSiteOrigin();
        const frontendOrigin = getFrontendOrigin();
        const candidates = navs.map((nav) => ({
          nav,
          items: nav?.content?.rendered
            ? parseNavHtml(nav.content.rendered, wpOrigin, frontendOrigin)
            : []
        }));

        const match =
          candidates.find(({ nav }) => (nav.slug || "").toLowerCase() === normalizedSlug) ||
          candidates.find(({ nav }) => (nav.title?.rendered || "").toLowerCase() === normalizedSlug) ||
          candidates.find(({ nav }) => (nav.title?.rendered || "").toLowerCase().includes(normalizedSlug));

        if (match) {
          setItems(match.items);
          return;
        }

        const fallback = candidates.find(({ items }) => items.length) || candidates[0];
        setItems(fallback?.items || []);
      } catch (e) {
        setError(true);
        setItems([]);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [normalizedSlug, lang]);

  return { items, loading, error };
}
